import * as React from 'react';
import { useEffect, useRef } from 'react';
import style from './PeaceFlame.module.css';

export type PeaceFlameAudioSource = 'mic' | 'silent' | 'external';

export interface PeaceFlameProps {
  /**
   * Active audio source. 'mic' requests getUserMedia; 'silent' uses an
   * internal simLevel() ember loop; 'external' expects an AnalyserNode in
   * `externalAnalyser` (reserved for future ultrasonic / venue-PA sync).
   */
  audioSource: PeaceFlameAudioSource;

  /** Required when audioSource === 'external'. */
  externalAnalyser?: AnalyserNode;

  /**
   * 'auto' attaches device-orientation when tiltState === 'granted'. On
   * desktop (no orientation events), moving the mouse over the container
   * also drives the tilt for testing.
   */
  tilt?: 'auto' | 'off';

  /** Host-managed permission state for DeviceOrientation. */
  tiltState?: 'unknown' | 'granted' | 'denied';

  /** Fired when the component obtains a definitive tilt permission outcome. */
  onTiltPermission?: (state: 'granted' | 'denied') => void;

  /** Fired when the component obtains a definitive mic permission outcome. */
  onMicPermission?: (state: 'granted' | 'denied') => void;

  /**
   * CSS scale applied to the canvas wrapper (origin upper-flame). 1 = whole
   * torch visible; ~3.5 zooms into the flame.
   */
  zoom?: number;

  /** Forces a pause regardless of visibility/intersection. */
  paused?: boolean;

  /**
   * Plan 001 Phase 1.1 polish — when false, the torch is drawn (brass shaft,
   * handle, lip) but NO flame particles are emitted, and existing particles
   * are cleared so the torch reads as unlit. Flip to true at the ignition
   * moment (the incoming torch "kisses" yours); a brief 800ms ignition boost
   * automatically follows so the flame puffs up larger before settling.
   * Default true (backwards compatible with all existing callers).
   */
  lit?: boolean;

  /** Accessibility label for the canvas. */
  ariaLabel?: string;

  className?: string;
}

// Reference dimensions of the original prototype — used as the baseline for
// geometry. Live canvas is sized to its container; geometry scales by H/REF_H.
const REF_W = 320;
const REF_H = 450;

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  size: number;
  hue: number;
  sat: number;
  lCore: number;
  lMid: number;
  lEdge: number;
  mode: 'normal' | 'spiral' | 'burst' | 'ember';
  spiralPhase: number;
  spiralSpeed: number;
  lean: number;
  decay: number;
}

interface Ring {
  x: number;
  y: number;
  r: number;
  maxR: number;
  life: number;
  hue: number;
}

interface Geometry {
  W: number;
  H: number;
  scale: number;
  cx: number;
  shaftW: number;
  torchBottom: number;
  torchTotal: number;
  lipTopY: number;
  brassBottomStart: number;
  brassBottomEnd: number;
  handleTop: number;
  handleBottom: number;
  brassUpperStart: number;
  p80Y: number;
  p80W: number;
  flareTopW: number;
  flareTopY: number;
  flameOrigin: number;
}

/**
 * Geometry is computed in canvas-native pixels. Zoom changes BOTH the torch's
 * size AND its vertical position — at zoom=1 the whole torch is visible with
 * its bottom near the canvas bottom (prototype layout). At zoom=1.5 the
 * torch is 1.5x bigger AND its flame emit point slides down to ~90% of the
 * viewport so the flame can rise across the entire screen.
 */
function computeGeometry(W: number, H: number, zoom = 1): Geometry {
  const baseScale = H / REF_H;
  const scale = baseScale * zoom;

  // Linear interp of flame's vertical position:
  //   zoom=1   → flameYRatio ≈ 0.244 (lifted from the original 0.289 so the
  //              torch handle/stem sits comfortably above the BefriendSheet
  //              peek rather than touching it)
  //   zoom=1.5 → flameYRatio = 0.90 (flame emit at 10% from viewport bottom)
  const idleFlameRatio = (REF_H - 340) / REF_H;
  const zoomedFlameRatio = 0.9;
  const zoomT = Math.max(0, Math.min(1, (zoom - 1) / 0.5));
  const flameYRatio =
    idleFlameRatio + (zoomedFlameRatio - idleFlameRatio) * zoomT;

  // Lift the rendered torch within the canvas without moving the canvas layer.
  const torchLift = 54;
  const flameOrigin = H * flameYRatio - torchLift;

  // Derive the rest going DOWN from the flame emit point. At zoom>1 the torch
  // body extends below the canvas (off-screen by design).
  const lipTopY = flameOrigin + 5 * scale;
  const lipHeight = 8 * scale;
  const flareTopY = lipTopY + lipHeight;
  const torchTotal = 300 * scale;
  const torchBottom = lipTopY + torchTotal;
  const brassBottomStart = torchBottom;
  const brassBottomEnd = torchBottom - 12 * scale;
  const handleTop = torchBottom - torchTotal * 0.46;
  const handleBottom = brassBottomEnd;
  const brassUpperStart = handleTop;
  const brassHeight = brassUpperStart - lipTopY;
  const p80Y = brassUpperStart - brassHeight * 0.8;

  const shaftW = 7 * scale;
  const p80W = shaftW + 3 * scale;
  const flareTopW = 40 * scale;

  const cx = W / 2;

  return {
    W,
    H,
    scale,
    cx,
    shaftW,
    torchBottom,
    torchTotal,
    lipTopY,
    brassBottomStart,
    brassBottomEnd,
    handleTop,
    handleBottom,
    brassUpperStart,
    p80Y,
    p80W,
    flareTopW,
    flareTopY,
    flameOrigin,
  };
}

interface InternalState {
  audioCtx: AudioContext | null;
  analyser: AnalyserNode | null;
  dataArr: Uint8Array | null;
  mediaStream: MediaStream | null;
  ownsAnalyser: boolean;
  rafId: number;
  phase: number;
  prevEnergy: number;
  beatCooldown: number;
  beatFlash: number;
  energyHistory: number[];
  smoothBass: number;
  smoothMid: number;
  smoothTreble: number;
  smoothOverall: number;
  colorPhase: number;
  particles: Particle[];
  rings: Ring[];
  rawGamma: number;
  rawBeta: number;
  smoothGamma: number;
  smoothBeta: number;
  tiltHue: number;
  tiltLean: number;
  tiltIntensity: number;
  prevGamma: number;
  prevBeta: number;
  motionEnergy: number;
  tiltActive: boolean;
  mouseTiltActive: boolean;
  isHidden: boolean;
  isOutOfView: boolean;
  pausedProp: boolean;
  geom: Geometry;
  currentZoom: number;
  targetZoom: number;
  lastW: number;
  lastH: number;
  reducedMotion: boolean;
  /** Plan 001 Phase 1.1 polish: gates particle emission. False = torch
   *  hardware drawn but no flame. Default true. */
  litProp: boolean;
  /** performance.now() at the moment lit flipped false→true, or null when no
   *  ignition boost is currently in flight. While set, the spawn loop emits
   *  extra/larger particles to make the flame puff up briefly before settling. */
  ignitionBoostAt: number | null;
}

function newState(): InternalState {
  return {
    audioCtx: null,
    analyser: null,
    dataArr: null,
    mediaStream: null,
    ownsAnalyser: false,
    rafId: 0,
    phase: 0,
    prevEnergy: 0,
    beatCooldown: 0,
    beatFlash: 0,
    energyHistory: [],
    smoothBass: 0,
    smoothMid: 0,
    smoothTreble: 0,
    smoothOverall: 0,
    colorPhase: 0,
    particles: [],
    rings: [],
    rawGamma: 0,
    rawBeta: 0,
    smoothGamma: 0,
    smoothBeta: 0,
    tiltHue: 260,
    tiltLean: 0,
    tiltIntensity: 0,
    prevGamma: 0,
    prevBeta: 0,
    motionEnergy: 0,
    tiltActive: false,
    mouseTiltActive: false,
    isHidden: false,
    isOutOfView: false,
    pausedProp: false,
    geom: computeGeometry(REF_W, REF_H, 1),
    currentZoom: 1,
    targetZoom: 1,
    lastW: REF_W,
    lastH: REF_H,
    reducedMotion: false,
    litProp: true,
    ignitionBoostAt: null,
  };
}

/**
 * Audio-reactive Torch of Peace canvas component.
 *
 * - Canvas sizes itself to its container via ResizeObserver (no fixed 320×450
 *   rasterization). All geometry scales by H/450 so the torch keeps its
 *   prototype proportions at any container size, with crisp pixels.
 * - Pause/resume baked in (Page Visibility + IntersectionObserver + paused
 *   prop). MediaStream tracks are stopped (not just paused) on hide/unmount.
 * - Tilt input: real DeviceOrientation when tiltState='granted'; mouse-over
 *   simulation on desktop for testing.
 */
export function PeaceFlame({
  audioSource,
  externalAnalyser,
  tilt = 'auto',
  tiltState = 'unknown',
  onMicPermission,
  zoom = 1,
  paused = false,
  lit = true,
  ariaLabel = 'Audio-reactive flame of peace',
  className,
}: PeaceFlameProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const stateRef = useRef<InternalState>(newState());

  useEffect(() => {
    stateRef.current.pausedProp = paused;
  }, [paused]);

  // Plan 001 Phase 1.1 polish: mirror `lit` into state and detect the
  // false→true transition to fire a brief 800ms ignition boost (extra/larger
  // particles for the "puff up then settle" feel). When lit drops to false,
  // immediately drain existing particles so the torch reads as unlit.
  useEffect(() => {
    const s = stateRef.current;
    const prev = s.litProp;
    s.litProp = lit;
    if (!prev && lit) {
      s.ignitionBoostAt =
        typeof performance !== 'undefined' ? performance.now() : Date.now();
    } else if (prev && !lit) {
      s.particles.length = 0;
      s.rings.length = 0;
      s.ignitionBoostAt = null;
    }
  }, [lit]);

  // When the zoom prop changes, set a new target. The draw loop lerps
  // currentZoom toward targetZoom each frame and recomputes geometry, so the
  // zoom is smooth without any CSS transform.
  useEffect(() => {
    stateRef.current.targetZoom = zoom;
  }, [zoom]);

  // Respect the user's prefers-reduced-motion setting. Cached on stateRef so
  // the per-frame spawn/draw loops can read it without re-render cost.
  // Listens for live changes (rare but supported via matchMedia).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => {
      stateRef.current.reducedMotion = mq.matches;
    };
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // --- AudioContext + AnalyserNode lifecycle (mount + audioSource change) ---
  useEffect(() => {
    const s = stateRef.current;

    if (!s.audioCtx) {
      try {
        const Ctor: typeof AudioContext =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext })
            .webkitAudioContext;
        s.audioCtx = new Ctor();
      } catch {
        s.audioCtx = null;
      }
    }

    const ctx = s.audioCtx;
    if (!ctx) return;

    let cancelled = false;

    const setupSilent = () => {
      if (s.analyser && s.ownsAnalyser) {
        try {
          s.analyser.disconnect();
        } catch {
          /* noop */
        }
      }
      s.analyser = null;
      s.dataArr = null;
      s.ownsAnalyser = false;
      if (s.mediaStream) {
        s.mediaStream.getTracks().forEach((t) => t.stop());
        s.mediaStream = null;
      }
    };

    const setupExternal = () => {
      if (s.mediaStream) {
        s.mediaStream.getTracks().forEach((t) => t.stop());
        s.mediaStream = null;
      }
      if (externalAnalyser) {
        s.analyser = externalAnalyser;
        s.dataArr = new Uint8Array(externalAnalyser.frequencyBinCount);
        s.ownsAnalyser = false;
      } else {
        s.analyser = null;
        s.dataArr = null;
      }
    };

    const setupMic = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        // Chrome creates AudioContext in 'suspended' state per the autoplay
        // policy. The getUserMedia grant counts as a user gesture, so we can
        // resume here. Without this, getByteFrequencyData returns all zeros.
        if (ctx.state === 'suspended') {
          try {
            await ctx.resume();
          } catch {
            /* ignore — analyser may still work once context starts on its own */
          }
        }
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.7;
        src.connect(analyser);
        s.mediaStream = stream;
        s.analyser = analyser;
        s.dataArr = new Uint8Array(analyser.frequencyBinCount);
        s.ownsAnalyser = true;
        onMicPermission?.('granted');
      } catch {
        if (!cancelled) {
          onMicPermission?.('denied');
          setupSilent();
        }
      }
    };

    if (audioSource === 'mic') {
      void setupMic();
    } else if (audioSource === 'external') {
      setupExternal();
    } else {
      setupSilent();
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioSource, externalAnalyser]);

  // --- DeviceOrientation listener + desktop mouse-tilt simulation ---
  useEffect(() => {
    const s = stateRef.current;
    if (tilt !== 'auto' || tiltState !== 'granted') {
      s.tiltActive = false;
      s.mouseTiltActive = false;
      return;
    }
    s.tiltActive = true;

    const orientationHandler = (e: DeviceOrientationEvent) => {
      if (e.gamma !== null) s.rawGamma = e.gamma;
      if (e.beta !== null) s.rawBeta = e.beta - 45;
      // Real orientation event arrived — stop using mouse simulation.
      s.mouseTiltActive = false;
    };
    window.addEventListener('deviceorientation', orientationHandler);

    // Desktop fallback: mouse position on the container drives tilt for
    // testing when there's no real device orientation.
    const container = containerRef.current;
    let mouseHandler: ((e: MouseEvent) => void) | null = null;
    let leaveHandler: (() => void) | null = null;
    if (container) {
      mouseHandler = (e: MouseEvent) => {
        if (s.tiltActive === false) return;
        const rect = container.getBoundingClientRect();
        const xNorm = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
        const yNorm = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
        s.mouseTiltActive = true;
        s.rawGamma = xNorm * 45;
        s.rawBeta = yNorm * 45;
      };
      leaveHandler = () => {
        s.mouseTiltActive = false;
      };
      container.addEventListener('mousemove', mouseHandler);
      container.addEventListener('mouseleave', leaveHandler);
    }

    return () => {
      window.removeEventListener('deviceorientation', orientationHandler);
      if (container && mouseHandler)
        container.removeEventListener('mousemove', mouseHandler);
      if (container && leaveHandler)
        container.removeEventListener('mouseleave', leaveHandler);
      s.tiltActive = false;
      s.mouseTiltActive = false;
    };
  }, [tilt, tiltState]);

  // --- Page Visibility ---
  useEffect(() => {
    const handler = () => {
      stateRef.current.isHidden = document.hidden;
      const ctx = stateRef.current.audioCtx;
      if (!ctx) return;
      if (document.hidden && ctx.state === 'running') {
        void ctx.suspend();
      } else if (!document.hidden && ctx.state === 'suspended') {
        void ctx.resume();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  // --- IntersectionObserver: pause when fully off-screen ---
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        stateRef.current.isOutOfView = !entry.isIntersecting;
      },
      { threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // --- Canvas init + ResizeObserver + draw loop (mount, never re-runs) ---
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx2d = canvas.getContext('2d');
    if (!ctx2d) return;

    const s = stateRef.current;

    const sizeCanvas = () => {
      const rect = container.getBoundingClientRect();
      const W = Math.max(REF_W, Math.floor(rect.width));
      const H = Math.max(REF_H, Math.floor(rect.height));
      // Retina-aware: backing buffer at device-pixel resolution, geometry in
      // CSS pixels. ctx is scaled by dpr so draw calls in CSS-pixel space
      // produce sharp device-pixel output. Cap dpr at 3 to bound memory on
      // ultra-high-density displays.
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      const bW = Math.floor(W * dpr);
      const bH = Math.floor(H * dpr);
      if (canvas.width === bW && canvas.height === bH) return;
      canvas.width = bW;
      canvas.height = bH;
      // Reset transform then scale once. setTransform clears anything prior.
      ctx2d.setTransform(dpr, 0, 0, dpr, 0, 0);
      s.lastW = W;
      s.lastH = H;
      s.geom = computeGeometry(W, H, s.currentZoom);
      // Clear particles whose coordinates are now in stale positions.
      s.particles.length = 0;
      s.rings.length = 0;
    };

    sizeCanvas();
    const ro = new ResizeObserver(() => sizeCanvas());
    ro.observe(container);

    const simLevel = () => {
      s.phase += 0.015;
      return Math.max(
        0,
        0.03 + Math.sin(s.phase) * 0.02 + Math.sin(s.phase * 3.1) * 0.01
      );
    };

    const getBands = () => {
      if (!s.analyser || !s.dataArr) {
        const b = simLevel();
        return { bass: b * 0.8, mid: b, treble: b * 0.5, overall: b };
      }
      s.analyser.getByteFrequencyData(s.dataArr);
      const N = s.dataArr.length;
      const bE = Math.floor(N * 0.08);
      const mE = Math.floor(N * 0.4);
      let bS = 0;
      let mS = 0;
      let tS = 0;
      for (let i = 0; i < bE; i++) bS += s.dataArr[i];
      for (let i = bE; i < mE; i++) mS += s.dataArr[i];
      for (let i = mE; i < N; i++) tS += s.dataArr[i];
      const bass = bS / bE / 255;
      const mid = mS / (mE - bE) / 255;
      const treble = tS / (N - mE) / 255;
      return { bass, mid, treble, overall: (bass + mid + treble) / 3 };
    };

    const detectBeat = (energy: number): boolean => {
      s.energyHistory.push(energy);
      if (s.energyHistory.length > 30) s.energyHistory.shift();
      const avg =
        s.energyHistory.reduce((a, b) => a + b, 0) / s.energyHistory.length;
      s.beatCooldown = Math.max(0, s.beatCooldown - 1);
      if (
        energy > avg * 1.4 + 0.05 &&
        energy > s.prevEnergy * 1.15 &&
        s.beatCooldown <= 0
      ) {
        s.beatCooldown = 8;
        s.beatFlash = 1;
        s.prevEnergy = energy;
        return true;
      }
      s.prevEnergy = energy;
      s.beatFlash *= 0.88;
      return false;
    };

    const updateTilt = () => {
      if (!s.tiltActive && !s.mouseTiltActive) return;
      const gam = s.rawGamma;
      const bet = s.rawBeta;
      s.smoothGamma += (gam - s.smoothGamma) * 0.12;
      s.smoothBeta += (bet - s.smoothBeta) * 0.12;
      const dg = Math.abs(gam - s.prevGamma);
      const db = Math.abs(bet - s.prevBeta);
      s.motionEnergy = s.motionEnergy * 0.85 + (dg + db) * 0.15;
      s.prevGamma = gam;
      s.prevBeta = bet;
      const gamNorm = Math.max(-1, Math.min(1, s.smoothGamma / 40));
      const betNorm = Math.max(-1, Math.min(1, s.smoothBeta / 40));
      s.tiltHue = 260 + gamNorm * 140 + betNorm * 40;
      s.tiltHue = ((s.tiltHue % 360) + 360) % 360;
      s.tiltLean = gamNorm * 0.8;
      s.tiltIntensity = Math.min(1, s.motionEnergy / 15);
    };

    const drawTorchBody = (intensity: number) => {
      const g = s.geom;
      const {
        cx,
        shaftW,
        brassBottomEnd,
        brassBottomStart,
        handleTop,
        handleBottom,
        brassUpperStart,
        p80Y,
        p80W,
        flareTopW,
        flareTopY,
        lipTopY,
      } = g;
      // bottom brass
      ctx2d.beginPath();
      ctx2d.moveTo(cx - shaftW, brassBottomEnd);
      ctx2d.lineTo(cx - shaftW + 1, brassBottomStart - 3);
      ctx2d.quadraticCurveTo(
        cx - shaftW + 1,
        brassBottomStart,
        cx - shaftW + 4,
        brassBottomStart
      );
      ctx2d.lineTo(cx + shaftW - 4, brassBottomStart);
      ctx2d.quadraticCurveTo(
        cx + shaftW - 1,
        brassBottomStart,
        cx + shaftW - 1,
        brassBottomStart - 3
      );
      ctx2d.lineTo(cx + shaftW, brassBottomEnd);
      ctx2d.closePath();
      const bN = ctx2d.createLinearGradient(
        cx - shaftW - 2,
        0,
        cx + shaftW + 2,
        0
      );
      bN.addColorStop(0, '#5a4018');
      bN.addColorStop(0.5, '#f0d468');
      bN.addColorStop(1, '#5a4018');
      ctx2d.fillStyle = bN;
      ctx2d.fill();

      // handle
      ctx2d.beginPath();
      ctx2d.moveTo(cx - shaftW, handleTop);
      ctx2d.lineTo(cx - shaftW, handleBottom);
      ctx2d.lineTo(cx + shaftW, handleBottom);
      ctx2d.lineTo(cx + shaftW, handleTop);
      ctx2d.closePath();
      const hG = ctx2d.createLinearGradient(cx - shaftW, 0, cx + shaftW, 0);
      hG.addColorStop(0, '#2a1210');
      hG.addColorStop(0.3, '#5a2a1e');
      hG.addColorStop(0.5, '#7a3e2e');
      hG.addColorStop(0.7, '#5a2a1e');
      hG.addColorStop(1, '#2a1210');
      ctx2d.fillStyle = hG;
      ctx2d.fill();

      // upper brass with flare
      // Top corners are rounded and the top edge gently domes upward so the
      // flame doesn't sit on a flat lip. cornerRadius rounds the rim where
      // the side walls meet the top; domeHeight raises the center slightly.
      const cornerRadius = 5 * g.scale;
      const domeHeight = 4 * g.scale;
      ctx2d.beginPath();
      ctx2d.moveTo(cx - shaftW, brassUpperStart);
      ctx2d.lineTo(cx - p80W, p80Y);
      ctx2d.bezierCurveTo(
        cx - p80W - 1,
        p80Y - (p80Y - flareTopY) * 0.3,
        cx - flareTopW + 5,
        p80Y - (p80Y - flareTopY) * 0.7,
        cx - flareTopW,
        flareTopY
      );
      // Left wall climbs to just below the corner radius
      ctx2d.lineTo(cx - flareTopW, lipTopY + cornerRadius);
      // Round the top-left corner
      ctx2d.quadraticCurveTo(
        cx - flareTopW,
        lipTopY,
        cx - flareTopW + cornerRadius,
        lipTopY
      );
      // Dome across the top
      ctx2d.quadraticCurveTo(
        cx,
        lipTopY - domeHeight,
        cx + flareTopW - cornerRadius,
        lipTopY
      );
      // Round the top-right corner
      ctx2d.quadraticCurveTo(
        cx + flareTopW,
        lipTopY,
        cx + flareTopW,
        lipTopY + cornerRadius
      );
      // Right wall back down to the shoulder
      ctx2d.lineTo(cx + flareTopW, flareTopY);
      ctx2d.bezierCurveTo(
        cx + flareTopW - 5,
        p80Y - (p80Y - flareTopY) * 0.7,
        cx + p80W + 1,
        p80Y - (p80Y - flareTopY) * 0.3,
        cx + p80W,
        p80Y
      );
      ctx2d.lineTo(cx + shaftW, brassUpperStart);
      ctx2d.closePath();
      const bG = ctx2d.createLinearGradient(
        cx - flareTopW,
        0,
        cx + flareTopW,
        0
      );
      bG.addColorStop(0, '#5a4018');
      bG.addColorStop(0.12, '#8a6828');
      bG.addColorStop(0.3, '#c9a040');
      bG.addColorStop(0.45, '#e8c860');
      bG.addColorStop(0.5, '#f0d468');
      bG.addColorStop(0.55, '#e8c860');
      bG.addColorStop(0.7, '#c9a040');
      bG.addColorStop(0.88, '#8a6828');
      bG.addColorStop(1, '#5a4018');
      ctx2d.fillStyle = bG;
      ctx2d.fill();

      // lip — bright highlight along the domed rim
      ctx2d.strokeStyle = '#f0d870';
      ctx2d.lineWidth = 1.5;
      ctx2d.beginPath();
      ctx2d.moveTo(cx - flareTopW + cornerRadius + 1, lipTopY);
      ctx2d.quadraticCurveTo(
        cx,
        lipTopY - domeHeight,
        cx + flareTopW - cornerRadius - 1,
        lipTopY
      );
      ctx2d.stroke();
      // side rim shadows (stop above the new rounded corner so they don't
      // cross the curve)
      ctx2d.strokeStyle = 'rgba(90,64,24,0.7)';
      ctx2d.lineWidth = 1;
      ctx2d.beginPath();
      ctx2d.moveTo(cx - flareTopW, flareTopY);
      ctx2d.lineTo(cx - flareTopW, lipTopY + cornerRadius);
      ctx2d.stroke();
      ctx2d.beginPath();
      ctx2d.moveTo(cx + flareTopW, flareTopY);
      ctx2d.lineTo(cx + flareTopW, lipTopY + cornerRadius);
      ctx2d.stroke();

      // beat flash
      if (s.beatFlash > 0.05) {
        const bf = ctx2d.createRadialGradient(
          cx,
          lipTopY + 4,
          0,
          cx,
          lipTopY + 4,
          flareTopW + 10
        );
        bf.addColorStop(0, `rgba(220,200,255,${s.beatFlash * 0.6})`);
        bf.addColorStop(0.5, `rgba(160,120,240,${s.beatFlash * 0.3})`);
        bf.addColorStop(1, 'rgba(100,60,180,0)');
        ctx2d.fillStyle = bf;
        ctx2d.beginPath();
        ctx2d.arc(cx, lipTopY + 4, flareTopW + 10, 0, Math.PI * 2);
        ctx2d.fill();
      }

      // inner glow colored by tilt
      const glA = 0.15 + intensity * 0.5;
      const iG = ctx2d.createRadialGradient(
        cx,
        lipTopY + 4,
        0,
        cx,
        lipTopY + 4,
        flareTopW - 4
      );
      const tH = s.tiltActive || s.mouseTiltActive ? s.tiltHue : 260;
      iG.addColorStop(0, `hsla(${tH},70%,65%,${glA})`);
      iG.addColorStop(0.5, `hsla(${tH},60%,40%,${glA * 0.4})`);
      iG.addColorStop(1, 'rgba(60,30,120,0)');
      ctx2d.fillStyle = iG;
      ctx2d.beginPath();
      ctx2d.ellipse(cx, lipTopY + 4, flareTopW - 8, 8, 0, 0, Math.PI * 2);
      ctx2d.fill();
    };

    const spawn = (
      intensity: number,
      bass: number,
      treble: number,
      isBeat: boolean
    ) => {
      // Plan 001 Phase 1.1: when unlit, no flame.
      if (!s.litProp) return;

      const g = s.geom;
      const tiltOn = s.tiltActive || s.mouseTiltActive;
      const baseHue = tiltOn ? s.tiltHue : 260 + Math.sin(s.colorPhase) * 15;
      const lean = tiltOn ? s.tiltLean * 8 * g.scale : 0;
      const tiltBoost = tiltOn ? s.tiltIntensity * 0.3 : 0;
      const effectiveIntensity = intensity + tiltBoost;

      // Plan 001 Phase 1.1 ignition boost: 0..1 strength that decays over
      // 1800ms after the lit transition. Holds near 1.0 for the first ~500ms
      // (the flame stays puffed up while the user takes in the moment), then
      // eases out over the remaining time so the settling is gradual rather
      // than abrupt.
      const IGNITION_BOOST_MS = 1800;
      const IGNITION_HOLD_MS = 500;
      let ignitionBoost = 0;
      if (s.ignitionBoostAt !== null) {
        const now =
          typeof performance !== 'undefined' ? performance.now() : Date.now();
        const elapsed = now - s.ignitionBoostAt;
        if (elapsed >= IGNITION_BOOST_MS) {
          s.ignitionBoostAt = null;
        } else if (elapsed < IGNITION_HOLD_MS) {
          ignitionBoost = 1;
        } else {
          // Ease-out from 1 → 0 over (IGNITION_BOOST_MS − IGNITION_HOLD_MS).
          const t =
            (elapsed - IGNITION_HOLD_MS) /
            (IGNITION_BOOST_MS - IGNITION_HOLD_MS);
          ignitionBoost = 1 - t;
        }
      }

      let count;
      if (s.reducedMotion) {
        // Reduced-motion: gentle ember-only loop regardless of audio.
        // Render the torch with a calm flame; no bursts, no spirals,
        // no beat-driven count surge.
        count = Math.random() < 0.3 ? 1 : 0;
        s.beatFlash = 0; // suppress the radial cup flash too
      } else if (effectiveIntensity < 0.03)
        count = Math.random() < 0.15 ? 1 : 0;
      else if (effectiveIntensity < 0.1)
        count = 1 + Math.floor(Math.random() * 2);
      else if (effectiveIntensity < 0.3)
        count = 3 + Math.floor(effectiveIntensity * 8);
      else if (effectiveIntensity < 0.6)
        count = 6 + Math.floor(effectiveIntensity * 14);
      else count = 12 + Math.floor(effectiveIntensity * 16);
      if (isBeat && !s.reducedMotion) count += 6;

      // Ignition boost: up to +14 extra particles at the moment of lighting.
      if (ignitionBoost > 0) count += Math.round(14 * ignitionBoost);

      for (let i = 0; i < count; i++) {
        const spread = (10 + effectiveIntensity * 12) * g.scale;
        let size;
        if (effectiveIntensity < 0.05) size = (2 + Math.random() * 3) * g.scale;
        else if (effectiveIntensity < 0.2)
          size = (3 + Math.random() * 6) * g.scale;
        else if (effectiveIntensity < 0.5)
          size = (5 + Math.random() * 12 + effectiveIntensity * 10) * g.scale;
        else
          size = (8 + Math.random() * 15 + effectiveIntensity * 22) * g.scale;

        // Ignition boost grows particles up to ~1.7x for the puff-up effect.
        if (ignitionBoost > 0) size *= 1 + 0.7 * ignitionBoost;

        const speed =
          (-0.8 - Math.random() * 1.2 - effectiveIntensity * 5) * g.scale;
        let mode: Particle['mode'] = 'normal';
        if (s.reducedMotion) {
          // Force gentle ember mode regardless of audio intensity.
          mode = Math.random() < 0.6 ? 'ember' : 'normal';
        } else if (isBeat && Math.random() < 0.4) mode = 'burst';
        else if (effectiveIntensity > 0.3 && Math.random() < 0.2)
          mode = 'spiral';
        else if (effectiveIntensity < 0.08 && Math.random() < 0.3)
          mode = 'ember';

        const hueVar = (Math.random() - 0.5) * 25;
        const sat = Math.min(96, 78 + treble * 12 + (tiltOn ? 6 : 0));

        s.particles.push({
          x: g.cx + (Math.random() - 0.5) * spread + lean,
          y:
            g.flameOrigin +
            (mode === 'ember' ? -Math.random() * 40 * g.scale : 0),
          vx:
            (Math.random() - 0.5) * (mode === 'burst' ? 4 : 0.8) * g.scale +
            lean * 0.05,
          vy:
            mode === 'burst'
              ? speed * 1.5
              : mode === 'ember'
                ? (-0.3 - Math.random() * 0.3) * g.scale
                : speed,
          life: 1,
          size,
          hue: baseHue + hueVar,
          sat,
          lCore: 68,
          lMid: 46,
          lEdge: 28,
          mode,
          spiralPhase: Math.random() * Math.PI * 2,
          spiralSpeed: (Math.random() - 0.5) * 0.15,
          lean: lean * 0.08,
          decay:
            mode === 'ember'
              ? 0.005
              : mode === 'burst'
                ? 0.025
                : 0.014 + effectiveIntensity * 0.004,
        });
      }

      if (isBeat && bass > 0.3 && !s.reducedMotion) {
        s.rings.push({
          x: g.cx,
          y: g.flameOrigin,
          r: g.flareTopW * 0.5,
          maxR: g.flareTopW * 2 + bass * 40 * g.scale,
          life: 1,
          hue: baseHue,
        });
      }
    };

    const draw = () => {
      const isPaused = s.pausedProp || s.isHidden || s.isOutOfView;
      if (isPaused) {
        s.rafId = window.requestAnimationFrame(draw);
        return;
      }

      // Smoothly lerp the zoom each frame. Geometry is recomputed only when
      // the value actually changes (more than ~0.001) to avoid pointless work
      // when at rest. Lerp factor 0.025 ≈ 1.5s to reach ~95% of target at
      // 60fps — feels contemplative, matches the slow torch-reveal pacing
      // (see TorchOverlay.PRE_ZOOM_HOLD_MS comment).
      if (Math.abs(s.currentZoom - s.targetZoom) > 0.001) {
        s.currentZoom += (s.targetZoom - s.currentZoom) * 0.025;
        s.geom = computeGeometry(s.lastW, s.lastH, s.currentZoom);
      } else if (s.currentZoom !== s.targetZoom) {
        s.currentZoom = s.targetZoom;
        s.geom = computeGeometry(s.lastW, s.lastH, s.currentZoom);
      }
      const g = s.geom;

      const lv = getBands();
      const sm = 0.15;
      s.smoothBass += (lv.bass - s.smoothBass) * sm;
      s.smoothMid += (lv.mid - s.smoothMid) * sm;
      s.smoothTreble += (lv.treble - s.smoothTreble) * sm;
      s.smoothOverall += (lv.overall - s.smoothOverall) * sm;

      const intensity = s.smoothOverall;
      const bass = s.smoothBass;
      const treble = s.smoothTreble;
      const isBeat = detectBeat(lv.overall);
      s.colorPhase += 0.003 + intensity * 0.008;

      updateTilt();

      const fadeA = intensity < 0.05 ? 0.35 : 0.22 - intensity * 0.08;
      ctx2d.fillStyle = `rgba(7,6,14,${fadeA})`;
      ctx2d.fillRect(0, 0, g.W, g.H);

      spawn(intensity, bass, treble, isBeat);

      ctx2d.globalCompositeOperation = 'lighter';
      const tiltOn = s.tiltActive || s.mouseTiltActive;
      const lean = tiltOn ? s.tiltLean : 0;

      for (let i = s.particles.length - 1; i >= 0; i--) {
        const p = s.particles[i];
        if (p.mode === 'spiral') {
          p.spiralPhase += p.spiralSpeed;
          p.x += Math.sin(p.spiralPhase) * 1.2 * g.scale + p.lean;
          p.y += p.vy;
        } else if (p.mode === 'burst') {
          p.x += p.vx;
          p.y += p.vy;
          p.vx *= 0.96;
          p.vy *= 0.96;
          p.vy -= 0.02 * g.scale;
        } else if (p.mode === 'ember') {
          p.x += (Math.random() - 0.5) * 0.4 * g.scale + p.lean * 0.3;
          p.y += p.vy;
          p.vy -= 0.005 * g.scale;
        } else {
          p.x += p.vx + (Math.random() - 0.5) * 0.2 * g.scale + lean * g.scale;
          p.y += p.vy;
          p.vy -= 0.03 * g.scale;
        }
        p.life -= p.decay;
        if (p.life <= 0) {
          s.particles.splice(i, 1);
          continue;
        }
        const sz = p.size * p.life;
        const alpha = p.life * (p.mode === 'ember' ? 0.3 : 0.38);
        const gr = ctx2d.createRadialGradient(p.x, p.y, 0, p.x, p.y, sz);
        gr.addColorStop(
          0,
          `hsla(${p.hue + 15},${p.sat}%,${p.lCore}%,${alpha})`
        );
        gr.addColorStop(
          0.3,
          `hsla(${p.hue},${p.sat - 10}%,${p.lMid}%,${alpha * 0.7})`
        );
        gr.addColorStop(
          0.65,
          `hsla(${p.hue - 15},${p.sat - 20}%,${p.lEdge}%,${alpha * 0.3})`
        );
        gr.addColorStop(1, `hsla(${p.hue - 30},35%,18%,0)`);
        ctx2d.fillStyle = gr;
        ctx2d.beginPath();
        ctx2d.arc(p.x, p.y, sz, 0, Math.PI * 2);
        ctx2d.fill();
      }

      for (let i = s.rings.length - 1; i >= 0; i--) {
        const r = s.rings[i];
        r.r += (r.maxR - r.r) * 0.12;
        r.life -= 0.04;
        if (r.life <= 0) {
          s.rings.splice(i, 1);
          continue;
        }
        ctx2d.strokeStyle = `hsla(${r.hue},60%,70%,${r.life * 0.3})`;
        ctx2d.lineWidth = 2 * r.life;
        ctx2d.beginPath();
        ctx2d.arc(r.x, r.y, r.r, 0, Math.PI * 2);
        ctx2d.stroke();
      }
      ctx2d.globalCompositeOperation = 'source-over';

      if (intensity > 0.03) {
        const ambR = (30 + bass * 60 + intensity * 30) * g.scale;
        const tH = tiltOn ? s.tiltHue : 260;
        const aG = ctx2d.createRadialGradient(
          g.cx,
          g.flameOrigin - 20 * g.scale,
          0,
          g.cx,
          g.flameOrigin - 20 * g.scale,
          ambR
        );
        aG.addColorStop(0, `hsla(${tH},60%,55%,${0.05 + bass * 0.25})`);
        aG.addColorStop(0.5, `hsla(${tH},50%,35%,${0.03 + bass * 0.12})`);
        aG.addColorStop(1, 'rgba(50,25,110,0)');
        ctx2d.fillStyle = aG;
        ctx2d.beginPath();
        ctx2d.arc(g.cx, g.flameOrigin - 20 * g.scale, ambR, 0, Math.PI * 2);
        ctx2d.fill();
      }

      drawTorchBody(intensity);

      if (s.particles.length > 300)
        s.particles.splice(0, s.particles.length - 300);

      s.rafId = window.requestAnimationFrame(draw);
    };

    s.rafId = window.requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(s.rafId);
      s.rafId = 0;
      ro.disconnect();
      if (s.mediaStream) {
        s.mediaStream.getTracks().forEach((t) => t.stop());
        s.mediaStream = null;
      }
      if (s.audioCtx) {
        void s.audioCtx.suspend();
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={[style.container, className].filter(Boolean).join(' ')}
    >
      <div className={style.canvasWrapper}>
        <canvas
          ref={canvasRef}
          className={style.canvas}
          role="img"
          aria-label={ariaLabel}
        />
      </div>
    </div>
  );
}
